import { SegmentedControl, Spinner } from '@nexo/ui';
import { BarChart3, CalendarRange, Timer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { TeamHeader } from '../../layouts/TeamHeader';
import { useTeam } from '../../layouts/TeamContext';
import { useSprints, useTasks } from '../../lib/queries';
import { ActiveSprint } from './ActiveSprint';
import { BacklogView } from './BacklogView';
import { SprintReports } from './SprintReports';
import { daysLeft } from './sprintUtils';

type Tab = 'active' | 'plan' | 'reports';
const tabs: Tab[] = ['active', 'plan', 'reports'];

/** The Sprints module: the active sprint, planning (backlog) and reports. */
export function SprintsPage() {
  const { t } = useTranslation();
  const { team } = useTeam();
  const sprints = useSprints(team.id);
  const tasks = useTasks(team.id);
  const [params, setParams] = useSearchParams();
  const all = sprints.data ?? [];
  const active = all.find((s) => s.status === 'active');
  const planned = all.filter((s) => s.status === 'planned');
  const requested = params.get('tab') as Tab | null;
  // Without an active sprint there is nothing to follow: open planning.
  const tab: Tab = requested && tabs.includes(requested) ? requested : active ? 'active' : 'plan';
  const setTab = (next: Tab) =>
    setParams((p) => {
      p.set('tab', next);
      return p;
    });
  const left = active ? daysLeft(active) : null;

  return (
    <>
      <TeamHeader>
        <h1 className="truncate text-[15px] font-semibold">{t('Sprints')}</h1>
        {active && (
          <span className="truncate text-[13px] text-muted max-sm:hidden">
            {active.name}
            {left !== null &&
              left >= 0 &&
              ` · ${left === 0 ? t('último día') : t('{{n}} d', { n: left })}`}
          </span>
        )}
        <SegmentedControl<Tab>
          label={t('Sección')}
          value={tab}
          onChange={setTab}
          className="ml-auto"
          size="sm"
          options={[
            { value: 'active', label: t('Sprint activo'), icon: <Timer /> },
            { value: 'plan', label: t('Planificación'), icon: <CalendarRange /> },
            { value: 'reports', label: t('Informes'), icon: <BarChart3 /> },
          ]}
        />
      </TeamHeader>
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        {sprints.isPending || tasks.isPending ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : tab === 'active' ? (
          <ActiveSprint
            sprint={active}
            tasks={tasks.data ?? []}
            planned={planned}
            onPlan={() => setTab('plan')}
          />
        ) : tab === 'plan' ? (
          <BacklogView tasks={tasks.data ?? []} />
        ) : (
          <SprintReports sprints={all} tasks={tasks.data ?? []} />
        )}
      </div>
    </>
  );
}
